import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { 
  Mail, RefreshCw, Check, X, AlertTriangle, Search, 
  ChevronDown, ChevronUp, Clock, Phone, Send, Clipboard
} from 'lucide-react';

const ResponseCheck = () => {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({ 
    emailConfigured: false, 
    pendingResponseChecks: 0,
    providerName: null,
    capabilities: { send: false, read: false, draft: false }
  });
  const [pendingItems, setPendingItems] = useState([]);
  const [expandedItem, setExpandedItem] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parsedResult, setParsedResult] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [checkingResponses, setCheckingResponses] = useState(false);

  useEffect(() => {
    fetchStatus();
    fetchPendingItems();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await api.get('/response-check/status');
      setStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch status:', err);
    }
  };

  const fetchPendingItems = async () => {
    setLoading(true);
    try {
      const response = await api.get('/response-check/pending');
      setPendingItems(response.data.items || []);
      setStatus(prev => ({ ...prev, emailConfigured: response.data.emailConfigured }));
    } catch (err) {
      console.error('Failed to fetch pending items:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Smart check responses - automatically uses email fetch if configured,
   * falls back to time-based progression if not
   */
  const handleCheckResponses = async () => {
    setCheckingResponses(true);
    try {
      const response = await api.post('/response-check/check');
      const r = response.data;
      
      if (r.mode === 'EMAIL_FETCH') {
        // Email-based results
        let message = `✅ Response Check Complete (Email)\n\n`;
        message += `📋 Checked: ${r.checked} items\n`;
        message += `📬 Responses found: ${r.responsesFound}\n`;
        message += `📝 Tasks created: ${r.tasksCreated || 0}\n`;
        message += `⏭️ Skipped (no email): ${r.skipped || 0}\n`;
        
        if (r.foundItems && r.foundItems.length > 0) {
          message += `\n📧 Found responses:\n`;
          r.foundItems.forEach(item => {
            const typeEmoji = {
              'POSITIVE': '✅',
              'DELAY': '⚠️',
              'SHORT_CLOSE': '❌',
              'NEEDS_REVIEW': '🔍',
              'NO_INFO': '❓',
              'UNKNOWN': '❓'
            };
            const emoji = typeEmoji[item.responseType] || '📧';
            message += `\n${emoji} PO ${item.poNumber} Line ${item.lineNumber} - ${item.responseType || 'UNKNOWN'}`;
            
            if (item.messageGist) {
              message += `\n   💬 "${item.messageGist}"`;
            }
            if (item.newShipDate) {
              message += `\n   📅 New ship date: ${new Date(item.newShipDate).toLocaleDateString()}`;
            }
            if (item.trackingNumber) {
              message += `\n   📦 Tracking: ${item.trackingNumber}`;
            }
            message += `\n`;
          });
        }
        
        if (r.errors && r.errors.length > 0) {
          message += `\n⚠️ Errors: ${r.errors.length}`;
        }
        
        alert(message);
      } else {
        // Time-based results
        let message = `✅ Response Check Complete (Time-Based)\n\n`;
        message += `📋 Items progressed: ${r.itemsProgressed}\n\n`;
        
        if (r.details) {
          message += `Day 1 → Day 2: ${r.details.day1ToDay2}\n`;
          message += `Day 2 → Day 3: ${r.details.day2ToDay3}\n`;
          message += `Day 3 → Escalate: ${r.details.day3ToEscalate}\n`;
        }
        
        if (r.progressedItems && r.progressedItems.length > 0) {
          message += `\n📋 Progressed items:\n`;
          r.progressedItems.slice(0, 10).forEach(item => {
            message += `• PO ${item.poNumber} Line ${item.lineNumber}: ${item.oldStatus} → ${item.newStatus}\n`;
          });
          if (r.progressedItems.length > 10) {
            message += `... and ${r.progressedItems.length - 10} more`;
          }
        }
        
        alert(message);
      }
      
      fetchPendingItems();
    } catch (err) {
      alert(`❌ Failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setCheckingResponses(false);
    }
  };

  const handleExpandItem = (item) => {
    if (expandedItem?.taskId === item.taskId) {
      setExpandedItem(null);
      setResponseText('');
      setParsedResult(null);
    } else {
      setExpandedItem(item);
      setResponseText('');
      setParsedResult(null);
    }
  };

  const handleParseResponse = async () => {
    if (!responseText.trim() || !expandedItem) return;
    
    setParsing(true);
    try {
      const response = await api.post('/response-check/parse', {
        responseText: responseText.trim(),
        taskId: expandedItem.taskId,
        poLineId: expandedItem.poLineId
      });
      setParsedResult(response.data.parsed);
    } catch (err) {
      alert(`❌ Parse failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setParsing(false);
    }
  };

  const handleConfirmResponse = async (noResponse = false) => {
    if (!expandedItem) return;
    
    setConfirming(true);
    try {
      const response = await api.post('/response-check/confirm', {
        taskId: expandedItem.taskId,
        poLineId: expandedItem.poLineId,
        parsed: noResponse ? null : parsedResult,
        noResponse
      });
      
      alert(`✅ PO ${response.data.poNumber} Line ${response.data.lineNumber} updated to: ${response.data.newStatus}`);
      
      // Reset and refresh
      setExpandedItem(null);
      setResponseText('');
      setParsedResult(null);
      fetchPendingItems();
    } catch (err) {
      alert(`❌ Confirm failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setConfirming(false);
    }
  };

  const getStatusBadgeColor = (status) => {
    if (status.includes('DAY1')) return 'primary';
    if (status.includes('DAY2')) return 'warning';
    if (status.includes('DAY3')) return 'warning';
    if (status.includes('DAY4') || status.includes('ESCALATE')) return 'danger';
    if (status.includes('RESPONDED')) return 'success';
    return 'secondary';
  };

  const getResponseTypeBadge = (type) => {
    const colors = {
      'POSITIVE': 'success',
      'DELAY': 'warning',
      'SHORT_CLOSE': 'danger',
      'PRICE_ISSUE': 'danger',
      'NO_INFO': 'secondary'
    };
    return colors[type] || 'secondary';
  };

  // Determine button text based on email configuration
  const canReadEmail = status.emailConfigured && status.capabilities?.read;
  const checkButtonText = checkingResponses 
    ? 'Checking...' 
    : (canReadEmail ? 'Check Responses' : 'Run Daily Check');
  const checkButtonIcon = checkingResponses 
    ? <RefreshCw size={16} className="animate-spin" /> 
    : (canReadEmail ? <Mail size={16} /> : <Clock size={16} />);

  if (loading) {
    return <div className="loading"><div className="spinner" /></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '4px' }}>Response Check</h1>
          <p className="text-muted text-sm">Check supplier responses and update PO status</p>
        </div>
        <div className="flex gap-2">
          <button 
            className="btn btn-primary"
            onClick={handleCheckResponses}
            disabled={checkingResponses}
          >
            {checkButtonIcon}
            {checkButtonText}
          </button>
        </div>
      </div>

      {/* Status Banner */}
      <div className="card mb-6" style={{ 
        background: status.emailConfigured 
          ? (status.capabilities?.read ? '#d4edda' : '#e7f3ff') 
          : '#fff3cd' 
      }}>
        <div className="card-body" style={{ padding: '12px 16px' }}>
          <div className="flex items-center gap-2">
            {status.emailConfigured ? (
              status.capabilities?.read ? (
                <>
                  <Check size={18} style={{ color: '#155724' }} />
                  <span style={{ color: '#155724' }}>
                    <strong>{status.providerName || 'Email'} configured.</strong> Click "Check Responses" to search inbox for supplier replies.
                  </span>
                </>
              ) : (
                <>
                  <Mail size={18} style={{ color: '#004085' }} />
                  <span style={{ color: '#004085' }}>
                    <strong>{status.providerName || 'Email'} configured (Send only).</strong> This provider cannot read inbox - paste supplier responses manually below.
                  </span>
                </>
              )
            ) : (
              <>
                <AlertTriangle size={18} style={{ color: '#856404' }} />
                <span style={{ color: '#856404' }}>
                  <strong>Email not configured.</strong> Paste supplier responses manually below. Configure email in Settings → Plugins.
                </span>
              </>
            )}
          </div>
          {/* Capability badges */}
          {status.emailConfigured && (
            <div className="flex gap-2" style={{ marginTop: '8px' }}>
              <span className={`badge ${status.capabilities?.send ? 'badge-success' : 'badge-secondary'}`} 
                    style={{ fontSize: '0.7rem' }}>
                {status.capabilities?.send ? '✓' : '✗'} Send
              </span>
              <span className={`badge ${status.capabilities?.read ? 'badge-success' : 'badge-secondary'}`}
                    style={{ fontSize: '0.7rem' }}>
                {status.capabilities?.read ? '✓' : '✗'} Read Inbox
              </span>
              <span className={`badge ${status.capabilities?.draft ? 'badge-success' : 'badge-secondary'}`}
                    style={{ fontSize: '0.7rem' }}>
                {status.capabilities?.draft ? '✓' : '✗'} Drafts
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Pending Items Count */}
      <div className="card mb-4">
        <div className="card-body" style={{ padding: '16px' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div>
                <span className="text-muted text-sm">Pending Response Checks</span>
                <div style={{ fontSize: '1.5rem', fontWeight: '600' }}>{pendingItems.length}</div>
              </div>
            </div>
            <button className="btn btn-outline btn-sm" onClick={fetchPendingItems}>
              <RefreshCw size={14} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Pending Items List */}
      {pendingItems.length === 0 ? (
        <div className="card">
          <div className="card-body text-center" style={{ padding: '40px' }}>
            <Check size={48} className="text-success mb-4" style={{ opacity: 0.5 }} />
            <h3>All Caught Up!</h3>
            <p className="text-muted">No pending response checks. Click "Check Responses" to scan for updates.</p>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Items Pending Response Check</h2>
          </div>
          <div style={{ maxHeight: '600px', overflow: 'auto' }}>
            {pendingItems.map((item) => (
              <div key={item.taskId} style={{ borderBottom: '1px solid var(--border)' }}>
                {/* Item Header */}
                <div 
                  onClick={() => handleExpandItem(item)}
                  style={{ 
                    padding: '16px', 
                    cursor: 'pointer',
                    background: expandedItem?.taskId === item.taskId ? 'var(--bg-secondary)' : 'transparent'
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <strong>PO {item.poNumber}</strong>
                          <span className="text-muted">Line {item.lineNumber}</span>
                          <span className={`badge badge-${getStatusBadgeColor(item.currentStatus)}`}>
                            {item.currentStatus}
                          </span>
                          {item.sopDay >= 3 && (
                            <span className="badge badge-warning flex items-center gap-1">
                              <Phone size={10} /> Day {item.sopDay}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-muted">
                          {item.itemNumber} - {item.itemDescription}
                        </div>
                        <div className="text-sm text-muted">
                          Supplier: {item.supplierName || item.supplierCode} ({item.supplierEmail})
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {expandedItem?.taskId === item.taskId ? 
                        <ChevronUp size={20} /> : 
                        <ChevronDown size={20} />
                      }
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedItem?.taskId === item.taskId && (
                  <div style={{ padding: '0 16px 16px', background: 'var(--bg-secondary)' }}>
                    {/* Instructions */}
                    <div style={{ 
                      background: '#e7f3ff', 
                      border: '1px solid #b3d7ff',
                      borderRadius: '6px',
                      padding: '12px',
                      marginBottom: '16px',
                      fontSize: '0.875rem',
                      color: '#004085'
                    }}>
                      <strong>📌 Instructions:</strong> Copy the <u>full email content</u> from Outlook and paste below. 
                      Include any tables or attachment content for best parsing results.
                    </div>

                    {/* Response Input */}
                    <div className="form-group">
                      <label className="form-label flex items-center gap-2">
                        <Clipboard size={14} />
                        Paste Supplier Response
                      </label>
                      <textarea
                        className="form-input"
                        rows={6}
                        placeholder="Paste the email response from the supplier here..."
                        value={responseText}
                        onChange={(e) => {
                          setResponseText(e.target.value);
                          setParsedResult(null);
                        }}
                        style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                      />
                    </div>

                    {/* Parse Button */}
                    <div className="flex gap-2 mb-4">
                      <button 
                        className="btn btn-primary btn-sm"
                        onClick={handleParseResponse}
                        disabled={!responseText.trim() || parsing}
                      >
                        {parsing ? <RefreshCw size={14} className="animate-spin" /> : <Search size={14} />}
                        {parsing ? 'Parsing...' : 'Parse Response'}
                      </button>
                      <button 
                        className="btn btn-outline btn-sm"
                        onClick={() => handleConfirmResponse(true)}
                        disabled={confirming}
                      >
                        <X size={14} />
                        No Response Received
                      </button>
                    </div>

                    {/* Parsed Result */}
                    {parsedResult && (
                      <div style={{ 
                        background: 'white', 
                        border: '1px solid var(--border)', 
                        borderRadius: '8px',
                        padding: '16px',
                        marginBottom: '16px'
                      }}>
                        <h4 className="mb-3 flex items-center gap-2">
                          <Check size={16} className="text-success" />
                          Parsed Result
                        </h4>
                        
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                          <div>
                            <span className="text-sm text-muted">Response Type</span>
                            <div>
                              <span className={`badge badge-${getResponseTypeBadge(parsedResult.responseType)}`}>
                                {parsedResult.responseType}
                              </span>
                            </div>
                          </div>
                          
                          <div>
                            <span className="text-sm text-muted">Confidence</span>
                            <div className="font-medium">
                              {Math.round((parsedResult.confidence || 0) * 100)}%
                            </div>
                          </div>

                          {parsedResult.trackingNumber && (
                            <div>
                              <span className="text-sm text-muted">Tracking Number</span>
                              <div className="font-medium">{parsedResult.trackingNumber}</div>
                            </div>
                          )}

                          {parsedResult.estimatedShipDate && (
                            <div>
                              <span className="text-sm text-muted">Est. Ship Date</span>
                              <div className="font-medium">{parsedResult.estimatedShipDate}</div>
                            </div>
                          )}

                          {parsedResult.estimatedDeliveryDate && (
                            <div>
                              <span className="text-sm text-muted">Est. Delivery Date</span>
                              <div className="font-medium">{parsedResult.estimatedDeliveryDate}</div>
                            </div>
                          )}

                          {parsedResult.quantity && (
                            <div>
                              <span className="text-sm text-muted">Quantity</span>
                              <div className="font-medium">{parsedResult.quantity}</div>
                            </div>
                          )}
                        </div>

                        {parsedResult.notes && (
                          <div style={{ marginTop: '12px' }}>
                            <span className="text-sm text-muted">Notes</span>
                            <div className="font-medium">{parsedResult.notes}</div>
                          </div>
                        )}

                        {/* Confirm Buttons */}
                        <div className="flex gap-2" style={{ marginTop: '16px' }}>
                          <button 
                            className="btn btn-primary btn-sm"
                            onClick={() => handleConfirmResponse(false)}
                            disabled={confirming}
                          >
                            {confirming ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
                            {confirming ? 'Updating...' : 'Confirm & Update Status'}
                          </button>
                          <button 
                            className="btn btn-outline btn-sm"
                            onClick={() => setParsedResult(null)}
                            disabled={confirming}
                          >
                            Re-parse
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Action hints based on SOP Day */}
                    {item.sopDay >= 3 && (
                      <div style={{ 
                        background: '#fff3cd', 
                        border: '1px solid #ffc107',
                        borderRadius: '6px',
                        padding: '12px',
                        fontSize: '0.875rem',
                        color: '#856404'
                      }}>
                        <strong>Day {item.sopDay} Action Required:</strong>
                        {item.sopDay === 3 ? (
                          <span> Email + Phone call to supplier recommended.</span>
                        ) : (
                          <span> Escalate to Buyer if no response.</span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ResponseCheck;